// ChartWrapper component with automatic cleanup for SVG memory management
import { useEffect, useRef, memo } from 'react';
import { Box } from '@chakra-ui/react';

// Track all active charts for cleanup
const activeCharts = new WeakMap();

export const ChartWrapper = memo(({ children, chartId, ...props }) => {
  const containerRef = useRef(null);
  const cleanupRef = useRef(null);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    // Register cleanup function
    cleanupRef.current = () => {
      // Clean up SVG elements
      const svgElements = container.querySelectorAll('svg');
      svgElements.forEach(svg => {
        // Remove all event listeners
        const clone = svg.cloneNode(false);
        svg.parentNode?.replaceChild(clone, svg);
        
        // Clear SVG content
        while (svg.firstChild) {
          svg.removeChild(svg.firstChild);
        }
      });
      
      // Clean up canvas elements if any
      const canvasElements = container.querySelectorAll('canvas');
      canvasElements.forEach(canvas => {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      });
      
      // Remove resize observers
      if (window.ResizeObserver) {
        const resizeObservers = activeCharts.get(container);
        if (resizeObservers) {
          resizeObservers.forEach(observer => observer.disconnect());
          activeCharts.delete(container);
        }
      }
    };
    
    // Cleanup on unmount
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);
  
  // Register resize observer tracking
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Track new ResizeObservers
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element && node.tagName === 'SVG') {
              // Store reference for cleanup
              if (!activeCharts.has(container)) {
                activeCharts.set(container, new Set());
              }
            }
          });
        }
      });
    });
    
    observer.observe(container, {
      childList: true,
      subtree: true
    });
    
    return () => observer.disconnect();
  }, []);
  
  return (
    <Box ref={containerRef} position="relative" {...props}>
      {children}
    </Box>
  );
});

ChartWrapper.displayName = 'ChartWrapper';

// Utility function to clean up detached chart elements
export const cleanupDetachedCharts = () => {
  // Find all detached SVG elements
  const allSvgs = document.querySelectorAll('svg');
  allSvgs.forEach(svg => {
    if (!svg.isConnected && svg.parentNode) {
      svg.parentNode.removeChild(svg);
    }
  });
  
  // Clear any leaked Recharts instances
  if (window.__RECHARTS_GLOBAL__) {
    window.__RECHARTS_GLOBAL__ = undefined;
  }
};